/**
 * Keeps staff photo ZIP links in a managed cart-note block while exposing only
 * customer-authored text in the cart note textarea.
 */
(function initializePhotoOrderNote() {
  if (window.DearestPhotoOrderNote) return;

  const START = '--- DEAREST MOMENT PHOTO FILES ---';
  const END = '--- END PHOTO FILES ---';
  const WORKER_URL = 'https://dearest-moment-photo-upload.tieudigi.workers.dev';
  const managedPattern = new RegExp(`\\n?${START.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}[\\s\\S]*?${END.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\n?`, 'g');
  let scheduledSync = null;
  let syncInFlight = null;

  function customerText(note) {
    return String(note || '').replace(managedPattern, '\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  function photoSets(cart) {
    const seen = new Set();
    return (cart?.items || []).flatMap((item) => {
      const properties = item.properties || {};
      const token = properties._photo_manifest_token;
      const sessionId = properties._upload_session_id;
      const bulkZipUrl = properties._bulk_zip_url;
      if (bulkZipUrl && !seen.has(bulkZipUrl)) {
        seen.add(bulkZipUrl);
        return [{ bulk: true, token: bulkZipUrl, sessionId: properties._bulk_upload_id || 'bulk', title: 'Bulk order', url: bulkZipUrl }];
      }
      if (!token || seen.has(token)) return [];
      seen.add(token);
      const product = String(item.product_title || item.title || 'Photo set');
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` – ${item.variant_title}` : '';
      return [{
        token,
        sessionId: sessionId || token,
        title: `${product}${variant}`.replace(/[\r\n]+/g, ' ').trim(),
        photoCount: Number.parseInt(properties._photo_count, 10) || null,
        quantity: item.quantity || 1,
        url: `${WORKER_URL}/download/${encodeURIComponent(token)}`,
      }];
    });
  }

  /** One ZIP link for every photo set in the order (one folder per set). */
  function orderZipUrl(sets) {
    if (sets.length === 1) return sets[0].url;
    return `${WORKER_URL}/download/order/${sets.map((set) => encodeURIComponent(set.token)).join(',')}`;
  }

  function merge(customerNote, cart) {
    const cleanNote = customerText(customerNote);
    const all = photoSets(cart);
    if (!all.length) return cleanNote;
    const sets = all.filter((set) => !set.bulk);
    const blocks = [];
    if (sets.length) {
      const describe = (set, index) => {
        const details = [set.photoCount && `${set.photoCount} photo${set.photoCount === 1 ? '' : 's'}`, set.quantity > 1 && `qty ${set.quantity}`].filter(Boolean);
        const label = sets.length > 1 ? `Set ${index + 1} ` : '';
        return `${label}[${String(set.sessionId).slice(-6)}]: ${set.title}${details.length ? ` · ${details.join(' · ')}` : ''}`;
      };
      blocks.push(`📦 All order photos (ZIP): ${orderZipUrl(sets)}`);
      blocks.push(sets.map(describe).join('\n'));
    }
    all.filter((set) => set.bulk).forEach((set) => blocks.push(`📸 ${set.title} [${String(set.sessionId).slice(-6)}]: ${set.url}`));
    return [cleanNote, START, ...blocks, END].filter(Boolean).join('\n\n');
  }

  async function getCart(signal) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal });
    if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
    return response.json();
  }

  async function sync({ customerNote, signal } = {}) {
    if (syncInFlight) await syncInFlight.catch(() => {});
    syncInFlight = (async () => {
      const cart = await getCart(signal);
      const desiredNote = merge(customerNote === undefined ? cart.note : customerNote, cart);
      if (String(cart.note || '') === desiredNote) return cart;
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: desiredNote }),
        signal,
      });
      if (!response.ok) throw new Error(`Unable to save the photo download link (${response.status}).`);
      return response.json();
    })();
    try { return await syncInFlight; } finally { syncInFlight = null; }
  }

  function hideManagedNoteFromTextarea(root = document) {
    const textareas = [...(root.querySelectorAll?.('textarea[name="note"], #cart-note') || [])];
    if (root.matches?.('textarea[name="note"], #cart-note')) textareas.unshift(root);
    textareas.forEach((textarea) => {
      const visibleValue = customerText(textarea.value);
      if (textarea.value !== visibleValue) textarea.value = visibleValue;
    });
  }

  function scheduleSync() {
    clearTimeout(scheduledSync);
    scheduledSync = setTimeout(() => {
      sync().catch((error) => console.warn('[PhotoOrderNote] Background synchronization failed:', error));
    }, 350);
  }

  window.DearestPhotoOrderNote = { START, END, customerText, merge, orderZipUrl, sync, getCart, hideManagedNoteFromTextarea };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hideManagedNoteFromTextarea(), { once: true });
  } else {
    hideManagedNoteFromTextarea();
  }
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) hideManagedNoteFromTextarea(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:update', scheduleSync);
  document.addEventListener('cart:add', scheduleSync);
})();
